/**
 * What a retailer's refund reason code MEANS for the return fee.
 *
 * A refund arrives from a retailer carrying a reason code, and the code belongs to the
 * RETAILER rather than to 2W: "14" is "No response from the shop" on one account and
 * "Customer Support Appeasement" on another, and retailers add and rename codes without
 * telling anyone. So the meaning cannot live in a list in this file - it is decided once per
 * retailer by a person, stored against that retailer, and read back here.
 *
 * EVERY SETTLED REFUND IS WRITTEN TO SHOPIFY AND BILLED, whatever its code. The code decides
 * only the return fee: RETURN means goods came back and somebody handled them, so a fee is
 * owed; PRICE_ADJUSTMENT is money off an order the customer kept, or money back with nothing
 * handled (a lost parcel, a goodwill payment), so no fee.
 *
 * A CODE WITH NO SETTING (UNCLASSIFIED) FOLLOWS THE QUANTITY: units back make it a return,
 * none a price adjustment. A setting is only needed where the quantity gets it wrong - a lost
 * parcel carries quantity 1 although nothing came back.
 *
 * NEITHER is no longer offered. A row still holding it from before 2026-11-04 is written as a
 * price adjustment, and is shown and saved as one.
 *
 * These helpers are separated from the page because they are rules rather than rendering -
 * which row is urgent, what each answer commits 2W to, and how a response that may be shaped
 * several ways is read - and because that is what the suite in src/test covers.
 */

/**
 * The answers as the API stores them. NEITHER is listed only so a row still holding it can be
 * read; the API no longer accepts it.
 *
 * Frozen and spelled out rather than derived from the options below, because these strings
 * cross the wire: the PATCH route rejects anything outside this set, so a typo here is a
 * failed save rather than a wrong label.
 */
export const CLASSIFICATIONS = Object.freeze({
  RETURN: 'RETURN',
  PRICE_ADJUSTMENT: 'PRICE_ADJUSTMENT',
  NEITHER: 'NEITHER',
  UNCLASSIFIED: 'UNCLASSIFIED',
});

/**
 * How each answer reads on screen, and what choosing it commits 2W to.
 *
 * The consequence is in the option text itself, not only in a tooltip. This dropdown is the
 * control that decides whether a brand is charged a return fee, and somebody choosing between
 * "Return" and "Price adjustment" on an unfamiliar code needs the fee consequence in front of
 * them at the moment they choose.
 *
 * UNCLASSIFIED is offered as a choice rather than being a one-way door out of the park. A
 * decision made in error has to be reversible, and the only honest reversal is back to "not
 * decided" - the alternative is leaving a wrong answer in place because the dropdown offered
 * no way back, which bills a fee nobody chose.
 */
export const CLASSIFICATION_OPTIONS = [
  {
    value: CLASSIFICATIONS.RETURN,
    label: 'Return — goods came back, so a return fee is owed',
  },
  {
    value: CLASSIFICATIONS.PRICE_ADJUSTMENT,
    label: 'Price adjustment — the customer kept the order, so no return fee',
  },
  {
    value: CLASSIFICATIONS.UNCLASSIFIED,
    label: 'No setting — follows the quantity: items back is a return, none is a price adjustment',
  },
];

/**
 * The cost of leaving a code undecided, in one sentence, in the words a person would use.
 *
 * One constant so that the banner above the table and the undecided row itself cannot drift
 * apart into two different accounts of the same rule.
 */
export const UNCLASSIFIED_COST =
  'Refunds on a code with no setting are still written to Shopify and billed: with items back '
  + 'they are a return and earn a return fee, with none they are a price adjustment and earn no fee.';

/** The badge an undecided row carries, kept out of the maps below so it cannot be edited away. */
const UNDECIDED_BADGE = { tone: undefined, label: 'No setting — by quantity' };

/**
 * The stored answer as a badge.
 *
 * The three decided answers are toned apart rather than all shown as neutral text, because
 * two of them mean no fee and one means a fee, and that is the difference somebody is
 * scanning the column for. Anything the API sends that is not one of the four is shown as
 * itself in a neutral badge rather than mapped to a guess: a value this console does not
 * recognise is a fact about the data, and dressing it up as "Not decided" would claim a park
 * that is not in the database.
 */
export function classificationBadge(classification) {
  if (classification === CLASSIFICATIONS.RETURN) {
    return { tone: 'info', label: 'Return — fee owed' };
  }
  if (classification === CLASSIFICATIONS.PRICE_ADJUSTMENT) {
    return { tone: 'success', label: 'Price adjustment — no fee' };
  }
  // Set before NEITHER was withdrawn; written as a price adjustment, so shown as one.
  if (classification === CLASSIFICATIONS.NEITHER) {
    return { tone: 'success', label: 'Price adjustment — no fee' };
  }
  if (classification === CLASSIFICATIONS.UNCLASSIFIED) {
    return UNDECIDED_BADGE;
  }
  return { tone: undefined, label: classification ? String(classification) : 'Unknown' };
}

/**
 * Whether this row is one of the ones somebody still has to decide.
 *
 * Asked as "is it one of the three DECIDED answers", not as "does it read UNCLASSIFIED", so
 * that a value this console does not recognise counts as undecided. Only the three decided
 * answers carry a billing consequence the classifier can act on; anything else parks the
 * refund either way, and treating an unknown value as decided would hide the row that most
 * needs looking at.
 */
export function isUndecided(row) {
  return !row || row.classification !== CLASSIFICATIONS.RETURN
    && row.classification !== CLASSIFICATIONS.PRICE_ADJUSTMENT
    && row.classification !== CLASSIFICATIONS.NEITHER;
}

/** How many refunds on this retailer are parked waiting for a person. */
export function undecidedCount(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((row) => isUndecided(row)).length;
}

/**
 * Undecided codes first, then by code.
 *
 * The list route already answers unclassified-first, so this is not a correction of the
 * server - it is what keeps that ordering TRUE after a save. Deciding a code changes its
 * class in place without a re-read, and a row that has just stopped being urgent sitting at
 * the top of an urgency-ordered list teaches people that the order means nothing.
 *
 * Codes are compared numerically-aware because retailers issue both "2" and "14" as codes and
 * plain string order puts "14" first, which reads as a sorting fault on the one column whose
 * values a person is matching against a retailer's own documentation. Sorting is done on a
 * copy: the caller's array is the fetched response, and mutating it would reorder the rows
 * React is keyed on during a save.
 */
export function sortReasonRows(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const aUndecided = isUndecided(a);
    const bUndecided = isUndecided(b);
    if (aUndecided !== bUndecided) return aUndecided ? -1 : 1;
    const aCode = a?.reasonCode == null ? '' : String(a.reasonCode);
    const bCode = b?.reasonCode == null ? '' : String(b.reasonCode);
    return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' });
  });
}

/**
 * The rows out of a list response, whatever the route wraps them in.
 *
 * The route is being built against the same contract as this screen and that contract fixes
 * the ROW shape, not the envelope, so this accepts the three shapes the rest of this console
 * already receives from its own admin routes - a bare array, `{ refundReasons }`, `{ rows }` -
 * rather than binding the screen to whichever one ships. A response that carries none of them
 * yields an empty list, which the table reports as "none recorded yet" alongside the scan
 * action, rather than throwing on `.map` of undefined.
 */
export function readReasonRows(response) {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];
  for (const key of ['refundReasons', 'reasons', 'rows', 'data']) {
    if (Array.isArray(response[key])) return response[key];
  }
  return [];
}

/**
 * A count the discover response states either as a list or as a number.
 *
 * Same reason as readReasonRows: the contract says the scan returns what it added and what it
 * already knew, without fixing whether that is the codes themselves or a tally of them. An
 * absent or unreadable figure answers 0, so an unexpected shape produces a sentence that
 * undercounts rather than one reading "undefined codes added".
 */
function countOf(value) {
  if (Array.isArray(value)) return value.length;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

/**
 * What the scan found, as one sentence, plus whether it needs somebody.
 *
 * Added codes arrive with no setting and follow the quantity. The tone is informational: they
 * are worth a look - a lost parcel's quantity gets the fee wrong - but nothing is held back.
 */
export function describeDiscovery(result) {
  const added = countOf(result?.added ?? result?.addedCount ?? result?.created);
  const known = countOf(result?.existing ?? result?.knownCount ?? result?.known ?? result?.skipped);

  if (added === 0) {
    return {
      tone: 'success',
      message: known === 0
        ? 'The scan found no reason codes in the orders held for this retailer.'
        : `No new reason codes. All ${known} found in the stored orders are already recorded.`,
    };
  }

  const codes = Array.isArray(result?.added)
    ? result.added
      .map((item) => (typeof item === 'string' ? item : item?.reasonCode))
      .filter(Boolean)
    : [];

  const names = codes.length > 0 ? ` (${codes.join(', ')})` : '';
  const alreadyKnown = known > 0 ? ` ${known} were already recorded.` : '';

  return {
    tone: 'info',
    message: `Recorded ${added} new reason code${added === 1 ? '' : 's'}${names}, none with a setting.`
      + `${alreadyKnown} ${UNCLASSIFIED_COST}`,
  };
}
